import { RiAddLine } from '@remixicon/react';
import { cn } from '@/shared/lib/cn';
import { Button } from './button';
import { renderIcon, type IconLike } from './icon';

export interface EmptyStateProps {
  /** Outline glyph shown over the glow (65 px in `sm`, 96 px in `lg`). */
  icon: IconLike;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Small Primary button with `add-line` by default ("Qo'shish", "Ko'rsatkich qo'shish"). */
  action?: { label: React.ReactNode; icon?: IconLike | null; onClick: () => void; loading?: boolean };
  /**
   * `sm` — panels / popovers: 100 px glow, title 600 14/20, description 400 12/16 (max 246) — 248:6325, 317:7945.
   * `lg` — canvas: 256 px glow + haze, title 600 18/auto, description 500 13.5/auto (max 420) — 317:7806.
   */
  size?: 'sm' | 'lg';
  /** Hide the glow (e.g. inside tables). */
  plain?: boolean;
  children?: React.ReactNode;
  className?: string;
}

/**
 * The three blurred circles behind the glyph (#05b4ff, #0085ff, #35b5ec at 30 %, Figma layer blur 67.57).
 * The CSS blur was fitted against the exported PNG (351:6871): σ ≈ 28 px matches the drawn falloff better than
 * the ÷2 rule (33.8 px). The `lg` haze layer (background blur 60) is folded into a wider blur — a backdrop-filter
 * box would draw a visible square edge.
 */
function Glow({ size }: { size: 'sm' | 'lg' }) {
  const circle = 'absolute rounded-full opacity-30';
  return size === 'sm' ? (
    <>
      <span className={cn(circle, 'top-39 left-34 size-65 bg-other-05b4ff blur-[1.75rem]')} />
      <span className={cn(circle, 'top-39 left-1 size-65 bg-other-0085ff blur-[1.75rem]')} />
      <span className={cn(circle, '-top-4 left-18 size-65 bg-blue-1000 blur-[1.75rem]')} />
    </>
  ) : (
    <>
      <span className={cn(circle, 'top-112 left-104 size-96 bg-other-05b4ff blur-[2.5rem]')} />
      <span className={cn(circle, 'top-112 left-56 size-96 bg-other-0085ff blur-[2.5rem]')} />
      <span className={cn(circle, 'top-48 left-80 size-96 bg-blue-1000 blur-[2.5rem]')} />
    </>
  );
}

/** Empty state with the product glow (kit 67:12247 / 67:12490 / 248:6325, spec §4.16, 02-constructor §2.8). */
export function EmptyState({ icon, title, description, action, size = 'sm', plain, children, className }: EmptyStateProps) {
  const lg = size === 'lg';
  return (
    <div className={cn('flex flex-col items-center text-center', lg ? 'gap-16' : 'gap-6', className)}>
      <div aria-hidden className={cn('relative shrink-0', lg ? 'size-256' : 'size-100')}>
        {!plain && <Glow size={size} />}
        <span className={cn('absolute text-line-strong', lg ? 'top-80 left-80 size-96' : 'top-18 left-18 size-65')}>
          {renderIcon(icon, 'size-full', { outline: true })}
        </span>
      </div>
      <div className={cn('flex flex-col items-center', lg ? 'gap-16' : 'gap-16')}>
        <div className={cn('flex flex-col items-center', lg ? 'w-420 gap-16' : 'max-w-246 gap-4')}>
          <p className={cn('font-semibold text-fg', lg ? 'text-lg leading-auto tracking-normal' : 'text-sm')}>{title}</p>
          {description && (
            <p
              className={cn(
                'text-fg-secondary',
                lg ? 'text-[0.84375rem] leading-auto font-medium' : 'text-xs tracking-normal',
              )}
            >
              {description}
            </p>
          )}
        </div>
        {action && (
          <Button
            size="sm"
            variant="primary"
            leftIcon={action.icon === null ? undefined : (action.icon ?? RiAddLine)}
            loading={action.loading}
            onClick={action.onClick}
          >
            {action.label}
          </Button>
        )}
        {children}
      </div>
    </div>
  );
}
